import { useEffect, useState } from 'react';
import { apiClient, ADMIN_API_BASE } from '@infrastructure/api/client';
import { errorText } from '@infrastructure/api/errors';
import { formatDateTime } from '@app/lib/format';

interface KPI {
  messages: { today: number; failed_today: number };
  customers: { total: number; active: number; suspended: number; pending?: number };
  billing: { revenue_30d: string; customer_balances?: string };
  platform: {
    api_keys_active: number;
    providers?: { provider: string; balance: string | null; currency: string; fetched_at: string | null; error: string }[];
  };
}

function Card({ title, value, sub }: { title: string; value: string | number; sub?: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs uppercase tracking-wide text-slate-500">{title}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export function OverviewPage() {
  const [data, setData] = useState<KPI | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiClient
      .get(`${ADMIN_API_BASE}/overview/`)
      .then((r) => setData(r.data))
      .catch((e) => setError(errorText(e)));
  }, []);

  if (error) return <div className="text-sm text-red-700">Failed to load overview: {error}</div>;
  if (!data) return <div className="text-sm text-slate-500">Loading overview…</div>;

  return (
    <section>
      <h2 className="mb-4 text-xl font-semibold text-slate-900">Platform Overview</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card title="Messages today" value={data.messages.today} sub={`${data.messages.failed_today} failed`} />
        <Card
          title="Customers"
          value={data.customers.total}
          sub={`${data.customers.active} active · ${data.customers.pending ?? 0} pending · ${data.customers.suspended} suspended`}
        />
        <Card
          title="Card top-ups (30d)"
          value={`$${Number(data.billing.revenue_30d).toFixed(2)}`}
          sub={`net of refunds · customers hold $${Number(data.billing.customer_balances ?? 0).toFixed(2)} unspent`}
        />
        <Card title="Active API keys" value={data.platform.api_keys_active} />
        {(data.platform.providers ?? []).map((p) => (
          <Card
            key={p.provider}
            title={`${p.provider} balance`}
            value={p.balance === null ? '—' : `$${p.balance} ${p.currency}`}
            sub={p.error ? `Last check failed: ${p.error}` : p.fetched_at ? `as of ${formatDateTime(p.fetched_at)}` : 'never checked'}
          />
        ))}
      </div>
    </section>
  );
}
